import React from 'react';
import { Box, useTheme } from '@mui/material';
import { Button } from '@components/atoms/Button';
import { IconButton } from '@components/atoms/IconButton';
import { Typography } from '@components/atoms/Typography';
import { FileUploadStatus } from '@components/molecules';
import { FileUploadStatusWrapperProps } from './FileUploadStatusWrapper.props';
import {
  formatFileSize,
  getFileExtension,
  getStatusLabel,
} from './FileUploadStatusWrapper.utils';
import { HighlightOff, Replay } from '@mui/icons-material';

/**
 * FileUploadStatusWrapper - Smart wrapper component for file upload status display
 *
 * This wrapper component handles all business logic and state derivation for displaying
 * file upload status. It processes file data, determines status labels, manages conditional
 * rendering of actions (delete/retry), and delegates pure UI rendering to FileUploadStatus.
 *
 * @component
 * @example
 * ```tsx
 * <FileUploadStatusWrapper
 *   fileData={{ id: '123', file: fileObject }}
 *   status="uploading"
 *   progress={50}
 *   onDelete={(id) => handleDelete(id)}
 *   onRetry={(id) => handleRetry(id)}
 * />
 * ```
 *
 * Responsibilities:
 * - Format file size and timestamps
 * - Derive status labels based on upload state
 * - Conditionally render action buttons (cancel, retry)
 * - Control progress bar visibility
 * - Handle callbacks for user actions (onDelete, onRetry)
 */
export const FileUploadStatusWrapper: React.FC<
  FileUploadStatusWrapperProps
> = ({ fileData, status, progress = 0, onDelete, onRetry, sx }) => {
  const theme = useTheme();
  const { file, processedAt } = fileData;

  // Derived data
  const fileExtension = getFileExtension(file.name);
  const fileSize = formatFileSize(file.size);
  const statusLabel = getStatusLabel(status, fileSize, processedAt);

  // Business logic: Determine right-side content based on status
  const getRightContent = (): React.ReactNode => {
    switch (status) {
      case 'pending':
      case 'storing':
      case 'stored':
        return onDelete ? (
          <IconButton
            size="m"
            sx={{
              padding: theme.unit(0.5),
              color: theme.palette.M3.sys.light['on-surface-variant'],
            }}
            onClick={() => onDelete(fileData.id)}
            aria-label="Remove file"
          >
            <HighlightOff fontSize="small" />
          </IconButton>
        ) : null;

      case 'uploading':
        return onDelete ? (
          <IconButton
            size="m"
            sx={{
              padding: theme.unit(0.5),
              color: theme.palette.M3.sys.light['on-surface-variant'],
            }}
            onClick={() => onDelete(fileData.id)}
            aria-label="Cancel upload"
          >
            <HighlightOff fontSize="small" />
          </IconButton>
        ) : null;

      case 'failed':
      case 'error-processing':
      case 'storage-failed': {
        const errorMessage =
          status === 'error-processing'
            ? 'Error processing file.'
            : status === 'storage-failed'
              ? 'Failed to prepare file.'
              : 'Failed';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              sx={{
                color: theme.palette.State.error,
                ...theme.typography.M3.body.medium,
              }}
            >
              {errorMessage}
            </Typography>
            {onRetry && (
              <Button
                variant="outlined"
                size="s"
                startIcon={<Replay />}
                onClick={() => onRetry(fileData.id)}
              >
                Retry
              </Button>
            )}
          </Box>
        );
      }

      case 'size-too-large':
        return (
          <Typography
            sx={{
              color: theme.palette.State.error,
              ...theme.typography.M3.body.medium,
            }}
          >
            File too large
          </Typography>
        );

      case 'type-not-allowed':
        return (
          <Typography
            sx={{
              color: theme.palette.State.error,
              ...theme.typography.M3.body.medium,
            }}
          >
            Invalid file type
          </Typography>
        );

      case 'batch-size-exceeded':
        return (
          <Typography
            sx={{
              color: theme.palette.State.error,
              ...theme.typography.M3.body.medium,
            }}
          >
            Batch size exceeded
          </Typography>
        );

      case 'file-count-exceeded':
        return (
          <Typography
            sx={{
              color: theme.palette.State.error,
              ...theme.typography.M3.body.medium,
            }}
          >
            File count exceeded
          </Typography>
        );

      default:
        return null;
    }
  };

  // Business logic: Determine if progress bar should be shown
  const shouldShowProgressBar = status === 'storing' || status === 'uploading';

  return (
    <FileUploadStatus
      title={file.name}
      fileExtension={fileExtension}
      subTitle={statusLabel}
      progress={progress}
      showProgressBar={shouldShowProgressBar}
      rightContent={getRightContent()}
      sx={sx}
    />
  );
};

export default FileUploadStatusWrapper;
